"use client";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchCalendarData,
  type CalendarData,
  type CalendarDay,
} from "../lib/api";
import {
  AD_MONTHS,
  BS_MONTHS,
  convertADToDatePair,
  convertBSToDatePair,
  getADMonthForBSMonth,
  getADMonthRangeForBSMonth,
  getBSMonthForADMonth,
  getDisplayMonthYear as getDisplayMonthYearUtil,
  getDropdownDisplayValues as getDropdownDisplayValuesUtil,
  getMonthKey,
  getNextMonth,
  getPrevMonth,
  getTodayDatePair,
  normalizeDateString,
  toNepaliNumber,
  type DatePair,
} from "../lib/calendarUtils";
import { adToBS } from "../lib/dateConverter";
import { calendarDB } from "../lib/indexedDB";

// Re-export constants for backward compatibility
export { BS_MONTHS as months, AD_MONTHS as monthsEn, toNepaliNumber };

// ============================================================================
// TYPES
// ============================================================================

export interface UseNepaliDatepickerProps {
  year?: number; // AD year
  month?: number; // AD month (1-12)
  calendarMode?: "bs" | "ad";
  value?: string; // Date in "YYYY-MM-DD" format (always AD)
  onDateSelect?: (date: CalendarDay | null) => void;
  onMonthChange?: (year: number, month: number) => void;
  onCalendarModeChange?: (mode: "bs" | "ad") => void;
}

// ============================================================================
// HOOK
// ============================================================================

export const useNepaliDatepicker = ({
  year: initialYear,
  month: initialMonth,
  calendarMode: initialCalendarMode = "bs",
  value: rawValue,
  onDateSelect,
  onMonthChange,
  onCalendarModeChange,
}: UseNepaliDatepickerProps) => {
  // Normalize value prop to YYYY-MM-DD format
  const value = normalizeDateString(rawValue);

  // -------------------------------------------------------------------------
  // Calculate initial state from props
  // Priority: value > year/month > today
  // -------------------------------------------------------------------------
  const calculateInitialState = () => {
    // Priority 1: value prop (AD format)
    if (value) {
      const datePair = convertADToDatePair(value);
      if (datePair) {
        return {
          bsYear: datePair.bs.bs_y,
          bsMonth: datePair.bs.bs_m,
          primaryADYear: datePair.ad.ad_y,
          primaryADMonth: datePair.ad.ad_m,
          selectedBSKey: datePair.bsString,
          selectedADKey: datePair.adString,
        };
      }
    }

    // Priority 2: year/month props (AD format)
    if (initialYear && initialMonth) {
      const bsInfo = getBSMonthForADMonth(initialYear, initialMonth);
      if (bsInfo) {
        return {
          bsYear: bsInfo.bsYear,
          bsMonth: bsInfo.bsMonth,
          primaryADYear: initialYear,
          primaryADMonth: initialMonth,
          selectedBSKey: "",
          selectedADKey: "",
        };
      }
    }

    // Priority 3: Today's date
    const today = getTodayDatePair();
    if (today) {
      const adInfo = getADMonthForBSMonth(today.bs.bs_y, today.bs.bs_m);
      return {
        bsYear: today.bs.bs_y,
        bsMonth: today.bs.bs_m,
        primaryADYear: adInfo?.adYear ?? null,
        primaryADMonth: adInfo?.adMonth ?? null,
        selectedBSKey: "",
        selectedADKey: "",
      };
    }

    // Fallback
    const currentYear = new Date().getFullYear() + 57;
    return {
      bsYear: currentYear,
      bsMonth: 1,
      primaryADYear: null,
      primaryADMonth: null,
      selectedBSKey: "",
      selectedADKey: "",
    };
  };

  const initialState = calculateInitialState();

  // -------------------------------------------------------------------------
  // State
  // -------------------------------------------------------------------------
  const [currentBSYear, setCurrentBSYear] = useState(initialState.bsYear);
  const [currentBSMonth, setCurrentBSMonth] = useState(initialState.bsMonth);
  const [primaryADYear, setPrimaryADYear] = useState<number | null>(
    initialState.primaryADYear
  );
  const [primaryADMonth, setPrimaryADMonth] = useState<number | null>(
    initialState.primaryADMonth
  );
  const [selectedBSKey, setSelectedBSKey] = useState(
    initialState.selectedBSKey
  );
  const [selectedADKey, setSelectedADKey] = useState(
    initialState.selectedADKey
  );
  const [selectedDate, setSelectedDate] = useState("");

  // Track origin months for toggle consistency
  const [lastBSMonthBeforeADToggle, setLastBSMonthBeforeADToggle] = useState<{
    year: number;
    month: number;
  } | null>(null);
  const [lastADMonthBeforeBSToggle, setLastADMonthBeforeBSToggle] = useState<{
    year: number;
    month: number;
  } | null>(null);

  const [calendarData, setCalendarData] = useState<
    Record<string, CalendarData>
  >({});
  const [isLoading, setIsLoading] = useState(true);
  const [isYearLoading, setIsYearLoading] = useState(false);
  const [isMonthLoading, setIsMonthLoading] = useState(false);
  const [isInputLoading, setIsInputLoading] = useState(false);
  const [calendarMode, setCalendarMode] = useState<"bs" | "ad">(
    initialCalendarMode
  );
  const [isIndexedDBInitialized, setIsIndexedDBInitialized] = useState(false);
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const [showYearDropdown, setShowYearDropdown] = useState(false);

  const titleRef = useRef<HTMLDivElement>(null);
  const prevValueRef = useRef<string | null | undefined>(undefined);
  const isInitialMountRef = useRef(true);

  // -------------------------------------------------------------------------
  // IndexedDB Initialization
  // -------------------------------------------------------------------------
  useEffect(() => {
    const initDB = async () => {
      try {
        await calendarDB.init();
      } catch (error) {
        console.error("Failed to init IndexedDB:", error);
      } finally {
        setIsIndexedDBInitialized(true);
      }
    };
    initDB();
  }, []);

  // -------------------------------------------------------------------------
  // Data Fetching
  // -------------------------------------------------------------------------
  const fetchMonthData = useCallback(
    async (bsYear: number, bsMonth: number): Promise<CalendarData | null> => {
      try {
        const key = getMonthKey(bsYear, bsMonth);

        // Check memory cache
        if (calendarData[key]) {
          return calendarData[key];
        }

        // Check IndexedDB
        if (isIndexedDBInitialized) {
          const cached = await calendarDB.get(bsYear, bsMonth);
          if (cached) {
            setCalendarData((prev) => ({ ...prev, [key]: cached }));
            return cached;
          }
        }

        // Fetch from API
        const data = await fetchCalendarData(bsYear, bsMonth);
        if (data) {
          setCalendarData((prev) => ({ ...prev, [key]: data }));
          if (isIndexedDBInitialized) {
            await calendarDB.set(bsYear, bsMonth, data);
          }
        }
        return data;
      } catch (error) {
        console.error("Error fetching month data:", error);
        return null;
      }
    },
    [isIndexedDBInitialized, calendarData]
  );

  const fetchAllBSMonthsForADMonth = useCallback(
    async (adYear: number, adMonth: number): Promise<CalendarDay[]> => {
      if (adYear < 1921 || adYear > 2032) {
        return [];
      }

      const allDays: CalendarDay[] = [];
      const fetchedBSMonths = new Set<string>();

      const lastDayOfMonth = new Date(adYear, adMonth, 0).getDate();
      const targetDays = [1, 15, lastDayOfMonth];

      for (const adDay of targetDays) {
        try {
          const bsDate = adToBS(adYear, adMonth, adDay);
          if (bsDate) {
            const bsMonthKey = getMonthKey(bsDate.bs_y, bsDate.bs_m);
            if (!fetchedBSMonths.has(bsMonthKey)) {
              fetchedBSMonths.add(bsMonthKey);
              const data = await fetchMonthData(bsDate.bs_y, bsDate.bs_m);
              if (data?.days) {
                const daysInMonth = data.days.filter(
                  (day) => day.ad?.ad_y === adYear && day.ad?.ad_m === adMonth
                );
                allDays.push(...daysInMonth);
              }
            }
          }
        } catch (error) {
          // Skip invalid dates
        }
      }

      // Deduplicate and sort
      const uniqueDays = new Map<string, CalendarDay>();
      for (const day of allDays) {
        if (day.ad?.date) {
          uniqueDays.set(day.ad.date, day);
        }
      }

      return Array.from(uniqueDays.values()).sort((a, b) => {
        if (!a.ad || !b.ad) return 0;
        return a.ad.ad_d - b.ad.ad_d;
      });
    },
    [fetchMonthData]
  );

  // -------------------------------------------------------------------------
  // Initial Data Load
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!isIndexedDBInitialized) return;

    const loadInitialData = async () => {
      setIsLoading(true);
      await fetchMonthData(currentBSYear, currentBSMonth);

      // Prefetch adjacent months
      const { year: prevY, month: prevM } = getPrevMonth(
        currentBSYear,
        currentBSMonth
      );
      const { year: nextY, month: nextM } = getNextMonth(
        currentBSYear,
        currentBSMonth
      );
      fetchMonthData(prevY, prevM);
      fetchMonthData(nextY, nextM);

      setIsLoading(false);
    };

    loadInitialData();
  }, [isIndexedDBInitialized]);

  // -------------------------------------------------------------------------
  // Value Prop Effect
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!value || !isIndexedDBInitialized) {
      prevValueRef.current = value;
      return;
    }

    // Skip if value hasn't changed
    if (prevValueRef.current === value) {
      return;
    }

    const datePair = convertADToDatePair(value);
    if (!datePair) {
      prevValueRef.current = value;
      return;
    }

    // Always update selection keys
    setSelectedADKey(datePair.adString);
    setSelectedBSKey(datePair.bsString);
    setPrimaryADMonth(datePair.ad.ad_m);
    setPrimaryADYear(datePair.ad.ad_y);

    // Skip navigation on initial mount (let popup opening handle it)
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      prevValueRef.current = value;
      return;
    }

    // Navigate to date's month if needed
    const navigateToDate = async () => {
      const currentData =
        calendarData[getMonthKey(currentBSYear, currentBSMonth)];
      const dateInCurrentMonth = currentData?.days?.find(
        (d) =>
          d.bs.date === datePair.bsString || d.ad?.date === datePair.adString
      );

      if (!dateInCurrentMonth) {
        // Search nearby months
        const searchOrder = [
          { year: datePair.bs.bs_y, month: datePair.bs.bs_m },
          {
            year: datePair.bs.bs_y,
            month: datePair.bs.bs_m - 1 < 1 ? 12 : datePair.bs.bs_m - 1,
          },
          {
            year: datePair.bs.bs_y,
            month: datePair.bs.bs_m + 1 > 12 ? 1 : datePair.bs.bs_m + 1,
          },
        ];

        for (const { year, month } of searchOrder) {
          let data: CalendarData | null | undefined =
            calendarData[getMonthKey(year, month)];
          if (!data) {
            data = await fetchMonthData(year, month);
          }

          if (
            data?.days?.find(
              (d) =>
                d.bs.date === datePair.bsString ||
                d.ad?.date === datePair.adString
            )
          ) {
            setCurrentBSYear(year);
            setCurrentBSMonth(month);
            break;
          }
        }
      }
    };

    navigateToDate();
    prevValueRef.current = value;
  }, [value, isIndexedDBInitialized, fetchMonthData]);

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------
  const getDataForKey = useCallback(
    (bsYear: number, bsMonth: number) =>
      calendarData[getMonthKey(bsYear, bsMonth)],
    [calendarData]
  );

  const getADMonthRange = useCallback((): string | null => {
    if (calendarMode === "ad") return null;
    return getADMonthRangeForBSMonth(
      currentBSYear,
      currentBSMonth,
      calendarData
    );
  }, [calendarMode, currentBSYear, currentBSMonth, calendarData]);

  const getDisplayMonthYear = useCallback(() => {
    return getDisplayMonthYearUtil(
      calendarMode,
      currentBSYear,
      currentBSMonth,
      primaryADYear,
      primaryADMonth,
      calendarData
    );
  }, [
    calendarMode,
    currentBSYear,
    currentBSMonth,
    primaryADYear,
    primaryADMonth,
    calendarData,
  ]);

  const getDropdownDisplayValues = useCallback(() => {
    return getDropdownDisplayValuesUtil(
      calendarMode,
      currentBSYear,
      currentBSMonth,
      primaryADYear,
      primaryADMonth
    );
  }, [
    calendarMode,
    currentBSYear,
    currentBSMonth,
    primaryADYear,
    primaryADMonth,
  ]);

  // -------------------------------------------------------------------------
  // Event Handlers
  // -------------------------------------------------------------------------
  const handleDayClick = useCallback(
    (day: CalendarDay | null) => {
      if(!day) {
        onDateSelect?.(day);
        return;
      }

      setSelectedBSKey(day.bs.date);
      setSelectedADKey(day.ad.date);
      setSelectedDate(calendarMode === "bs" ? day.bs.date : day.ad.date);

      // Clear stored origin months - user has made a new explicit selection
      setLastBSMonthBeforeADToggle(null);
      setLastADMonthBeforeBSToggle(null);

      if (calendarMode === "ad" && day.ad) {
        setPrimaryADMonth(day.ad.ad_m);
        setPrimaryADYear(day.ad.ad_y);
      }

      onDateSelect?.(day);
    },
    [calendarMode, onDateSelect]
  );

  const handlePrevMonth = useCallback(async () => {
    setIsMonthLoading(true);
    try {
      if (calendarMode === "bs") {
        const { year, month } = getPrevMonth(currentBSYear, currentBSMonth);
        if (!getDataForKey(year, month)) {
          await fetchMonthData(year, month);
        }
        setCurrentBSYear(year);
        setCurrentBSMonth(month);
        onMonthChange?.(year, month);
      } else {
        // AD mode
        const display = getDisplayMonthYear();
        let targetADYear = display.yearNum;
        let targetADMonth = display.monthNum - 1;

        if (targetADMonth === 0) {
          targetADMonth = 12;
          targetADYear -= 1;
        }

        const bsInfo = getBSMonthForADMonth(targetADYear, targetADMonth);
        if (bsInfo) {
          if (!getDataForKey(bsInfo.bsYear, bsInfo.bsMonth)) {
            await fetchMonthData(bsInfo.bsYear, bsInfo.bsMonth);
          }
          setCurrentBSYear(bsInfo.bsYear);
          setCurrentBSMonth(bsInfo.bsMonth);
          setPrimaryADMonth(targetADMonth);
          setPrimaryADYear(targetADYear);
          onMonthChange?.(targetADYear, targetADMonth);
        }
      }
    } finally {
      setIsMonthLoading(false);
    }
  }, [
    calendarMode,
    currentBSYear,
    currentBSMonth,
    getDataForKey,
    fetchMonthData,
    getDisplayMonthYear,
    onMonthChange,
  ]);

  const handleNextMonth = useCallback(async () => {
    setIsMonthLoading(true);
    try {
      if (calendarMode === "bs") {
        const { year, month } = getNextMonth(currentBSYear, currentBSMonth);
        if (!getDataForKey(year, month)) {
          await fetchMonthData(year, month);
        }
        setCurrentBSYear(year);
        setCurrentBSMonth(month);
        onMonthChange?.(year, month);
      } else {
        // AD mode
        const display = getDisplayMonthYear();
        let targetADYear = display.yearNum;
        let targetADMonth = display.monthNum + 1;

        if (targetADMonth === 13) {
          targetADMonth = 1;
          targetADYear += 1;
        }

        const bsInfo = getBSMonthForADMonth(targetADYear, targetADMonth);
        if (bsInfo) {
          if (!getDataForKey(bsInfo.bsYear, bsInfo.bsMonth)) {
            await fetchMonthData(bsInfo.bsYear, bsInfo.bsMonth);
          }
          setCurrentBSYear(bsInfo.bsYear);
          setCurrentBSMonth(bsInfo.bsMonth);
          setPrimaryADMonth(targetADMonth);
          setPrimaryADYear(targetADYear);
          onMonthChange?.(targetADYear, targetADMonth);
        }
      }
    } finally {
      setIsMonthLoading(false);
    }
  }, [
    calendarMode,
    currentBSYear,
    currentBSMonth,
    getDataForKey,
    fetchMonthData,
    getDisplayMonthYear,
    onMonthChange,
  ]);

  const handleCalendarModeChange = useCallback(
    async (mode: "bs" | "ad") => {
      if (mode === calendarMode) return;

      if (mode === "ad") {
        // Switching BS → AD
        // Save current BS month as origin for when we toggle back
        setLastBSMonthBeforeADToggle({
          year: currentBSYear,
          month: currentBSMonth,
        });

        let targetADYear: number | null = null;
        let targetADMonth: number | null = null;

        // PRIORITY 1: Check if selected date is in current month - if so, use its AD month
        const isSelectedDateInCurrentMonth = (() => {
          if (!selectedBSKey) return false;
          const currentData = getDataForKey(currentBSYear, currentBSMonth);
          return (
            currentData?.days?.some((d) => d.bs.date === selectedBSKey) ?? false
          );
        })();

        if (isSelectedDateInCurrentMonth && selectedADKey) {
          const datePair = convertADToDatePair(selectedADKey);
          if (datePair) {
            targetADYear = datePair.ad.ad_y;
            targetADMonth = datePair.ad.ad_m;
          }
        }

        // PRIORITY 2: Check if we have a stored AD month from previous toggle (user toggled AD→BS→AD without selecting)
        if (!targetADYear && lastADMonthBeforeBSToggle) {
          // Check if the stored AD month overlaps with current BS month
          const currentData = getDataForKey(currentBSYear, currentBSMonth);
          const hasOverlap = currentData?.days?.some(
            (d) =>
              d.ad?.ad_y === lastADMonthBeforeBSToggle.year &&
              d.ad?.ad_m === lastADMonthBeforeBSToggle.month
          );

          if (hasOverlap) {
            // Restore to the AD month user was viewing before
            targetADYear = lastADMonthBeforeBSToggle.year;
            targetADMonth = lastADMonthBeforeBSToggle.month;
          }
          setLastADMonthBeforeBSToggle(null); // Clear after checking
        }

        // PRIORITY 3: Use the AD month that has majority days in current BS month
        if (!targetADYear) {
          const currentData = getDataForKey(currentBSYear, currentBSMonth);
          if (currentData?.days) {
            const monthCounts: Record<string, number> = {};
            for (const day of currentData.days) {
              if (day.ad?.ad_y && day.ad?.ad_m) {
                const key = `${day.ad.ad_y}-${day.ad.ad_m}`;
                monthCounts[key] = (monthCounts[key] || 0) + 1;
              }
            }

            let maxCount = 0;
            let majorityKey = "";
            for (const [key, count] of Object.entries(monthCounts)) {
              if (count > maxCount) {
                maxCount = count;
                majorityKey = key;
              }
            }

            if (majorityKey) {
              const [y, m] = majorityKey.split("-").map(Number);
              targetADYear = y;
              targetADMonth = m;
            }
          }
        }

        // PRIORITY 4: Fallback to conversion
        if (!targetADYear) {
          const adInfo = getADMonthForBSMonth(currentBSYear, currentBSMonth);
          if (adInfo) {
            targetADYear = adInfo.adYear;
            targetADMonth = adInfo.adMonth;
          }
        }

        if (targetADYear && targetADMonth) {
          setPrimaryADMonth(targetADMonth);
          setPrimaryADYear(targetADYear);
        }
      } else {
        // Switching AD → BS
        // Save current AD month as origin for when we toggle back
        if (primaryADYear !== null && primaryADMonth !== null) {
          setLastADMonthBeforeBSToggle({
            year: primaryADYear,
            month: primaryADMonth,
          });
        }

        let targetBSYear: number | null = null;
        let targetBSMonth: number | null = null;

        // PRIORITY 1: Check if selected date is in current AD month - if so, use its BS month
        const isSelectedDateInCurrentADMonth = (() => {
          if (
            !selectedADKey ||
            primaryADYear === null ||
            primaryADMonth === null
          )
            return false;
          const datePair = convertADToDatePair(selectedADKey);
          if (!datePair) return false;
          return (
            datePair.ad.ad_y === primaryADYear &&
            datePair.ad.ad_m === primaryADMonth
          );
        })();

        if (isSelectedDateInCurrentADMonth && selectedBSKey) {
          const datePair = convertADToDatePair(selectedADKey);
          if (datePair) {
            targetBSYear = datePair.bs.bs_y;
            targetBSMonth = datePair.bs.bs_m;
          }
        }

        // PRIORITY 2: Check if we have a stored BS month from previous toggle
        if (!targetBSYear && lastBSMonthBeforeADToggle) {
          // Check if the stored BS month overlaps with current AD month
          const originData = getDataForKey(
            lastBSMonthBeforeADToggle.year,
            lastBSMonthBeforeADToggle.month
          );
          const hasOverlap = originData?.days?.some(
            (d) => d.ad?.ad_y === primaryADYear && d.ad?.ad_m === primaryADMonth
          );

          if (hasOverlap) {
            // Restore to the BS month user was viewing before
            targetBSYear = lastBSMonthBeforeADToggle.year;
            targetBSMonth = lastBSMonthBeforeADToggle.month;
          }
          setLastBSMonthBeforeADToggle(null); // Clear after checking
        }

        // PRIORITY 3: Use conversion as fallback
        if (
          !targetBSYear &&
          primaryADYear !== null &&
          primaryADMonth !== null
        ) {
          const bsInfo = getBSMonthForADMonth(primaryADYear, primaryADMonth);
          if (bsInfo) {
            targetBSYear = bsInfo.bsYear;
            targetBSMonth = bsInfo.bsMonth;
          }
        }

        if (targetBSYear && targetBSMonth) {
          setCurrentBSYear(targetBSYear);
          setCurrentBSMonth(targetBSMonth);
          if (!getDataForKey(targetBSYear, targetBSMonth)) {
            await fetchMonthData(targetBSYear, targetBSMonth);
          }
        }
      }

      setCalendarMode(mode);
      onCalendarModeChange?.(mode);
    },
    [
      calendarMode,
      selectedADKey,
      selectedBSKey,
      currentBSYear,
      currentBSMonth,
      primaryADYear,
      primaryADMonth,
      lastBSMonthBeforeADToggle,
      lastADMonthBeforeBSToggle,
      getDataForKey,
      fetchMonthData,
      onCalendarModeChange,
    ]
  );

  const handleMonthSelect = useCallback(
    async (month: number) => {
      setIsMonthLoading(true);
      try {
        if (calendarMode === "bs") {
          if (!getDataForKey(currentBSYear, month)) {
            await fetchMonthData(currentBSYear, month);
          }
          setCurrentBSMonth(month);
          onMonthChange?.(currentBSYear, month);
        } else {
          const adYear = primaryADYear ?? getDisplayMonthYear().yearNum;
          const bsInfo = getBSMonthForADMonth(adYear, month);
          if (bsInfo) {
            if (!getDataForKey(bsInfo.bsYear, bsInfo.bsMonth)) {
              await fetchMonthData(bsInfo.bsYear, bsInfo.bsMonth);
            }
            setCurrentBSYear(bsInfo.bsYear);
            setCurrentBSMonth(bsInfo.bsMonth);
            setPrimaryADMonth(month);
            setPrimaryADYear(adYear);
            onMonthChange?.(adYear, month);
          }
        }
        setShowMonthDropdown(false);
      } finally {
        setIsMonthLoading(false);
      }
    },
    [
      calendarMode,
      currentBSYear,
      primaryADYear,
      getDataForKey,
      fetchMonthData,
      getDisplayMonthYear,
      onMonthChange,
    ]
  );

  const handleYearSelect = useCallback(
    async (year: number) => {
      setIsYearLoading(true);
      try {
        if (calendarMode === "bs") {
          if (!getDataForKey(year, currentBSMonth)) {
            await fetchMonthData(year, currentBSMonth);
          }
          setCurrentBSYear(year);
          onMonthChange?.(year, currentBSMonth);
        } else {
          const adMonth = primaryADMonth ?? getDisplayMonthYear().monthNum;
          const bsInfo = getBSMonthForADMonth(year, adMonth);
          if (bsInfo) {
            if (!getDataForKey(bsInfo.bsYear, bsInfo.bsMonth)) {
              await fetchMonthData(bsInfo.bsYear, bsInfo.bsMonth);
            }
            setCurrentBSYear(bsInfo.bsYear);
            setCurrentBSMonth(bsInfo.bsMonth);
            setPrimaryADMonth(adMonth);
            setPrimaryADYear(year);
            onMonthChange?.(year, adMonth);
          }
        }
        setShowYearDropdown(false);
      } finally {
        setIsYearLoading(false);
      }
    },
    [
      calendarMode,
      currentBSMonth,
      primaryADMonth,
      getDataForKey,
      fetchMonthData,
      getDisplayMonthYear,
      onMonthChange,
    ]
  );

  // Handle manual input date
  const handleInputDate = useCallback(
    async (
      inputValue: string,
      mode: "bs" | "ad"
    ): Promise<CalendarDay | null> => {
      if (!inputValue || inputValue.length < 10) return null;

      setIsInputLoading(true);
      try {
        let datePair: DatePair | null = null;

        if (mode === "bs") {
          datePair = convertBSToDatePair(inputValue);
        } else {
          datePair = convertADToDatePair(inputValue);
        }

        if (!datePair) return null;

        // Search for the date
        const searchOrder = [
          { year: datePair.bs.bs_y, month: datePair.bs.bs_m },
          {
            year: datePair.bs.bs_y,
            month: datePair.bs.bs_m - 1 < 1 ? 12 : datePair.bs.bs_m - 1,
          },
          {
            year: datePair.bs.bs_y,
            month: datePair.bs.bs_m + 1 > 12 ? 1 : datePair.bs.bs_m + 1,
          },
        ];

        for (const { year, month } of searchOrder) {
          let data: CalendarData | null | undefined =
            calendarData[getMonthKey(year, month)];
          if (!data) {
            data = await fetchMonthData(year, month);
          }

          if (data?.days) {
            const foundDay = data.days.find(
              (d) =>
                d.bs.date === datePair!.bsString ||
                d.ad?.date === datePair!.adString
            );

            if (foundDay) {
              setCurrentBSYear(year);
              setCurrentBSMonth(month);
              setSelectedBSKey(foundDay.bs.date);
              setSelectedADKey(foundDay.ad.date);
              setSelectedDate(
                mode === "bs" ? foundDay.bs.date : foundDay.ad.date
              );

              if (mode === "ad" && foundDay.ad) {
                setPrimaryADMonth(foundDay.ad.ad_m);
                setPrimaryADYear(foundDay.ad.ad_y);
              }

              return foundDay;
            }
          }
        }

        return null;
      } finally {
        setIsInputLoading(false);
      }
    },
    [calendarData, fetchMonthData]
  );

  // Navigate to value prop's month (called when popup opens)
  const navigateToValuePropMonth = useCallback(async () => {
    if (!value) return;

    const datePair = convertADToDatePair(value);
    if (!datePair) return;

    // Check if date is already visible
    const currentData = getDataForKey(currentBSYear, currentBSMonth);
    const dateVisible = currentData?.days?.find(
      (d) => d.bs.date === datePair.bsString || d.ad?.date === datePair.adString
    );

    if (dateVisible) return;

    // Navigate to the date's month
    const searchOrder = [
      { year: datePair.bs.bs_y, month: datePair.bs.bs_m },
      {
        year: datePair.bs.bs_y,
        month: datePair.bs.bs_m - 1 < 1 ? 12 : datePair.bs.bs_m - 1,
      },
      {
        year: datePair.bs.bs_y,
        month: datePair.bs.bs_m + 1 > 12 ? 1 : datePair.bs.bs_m + 1,
      },
    ];

    for (const { year, month } of searchOrder) {
      let data: CalendarData | null | undefined =
        calendarData[getMonthKey(year, month)];
      if (!data) {
        data = await fetchMonthData(year, month);
      }

      if (
        data?.days?.find(
          (d) =>
            d.bs.date === datePair.bsString || d.ad?.date === datePair.adString
        )
      ) {
        setCurrentBSYear(year);
        setCurrentBSMonth(month);
        break;
      }
    }
  }, [
    value,
    currentBSYear,
    currentBSMonth,
    calendarData,
    getDataForKey,
    fetchMonthData,
  ]);

  // -------------------------------------------------------------------------
  // Return
  // -------------------------------------------------------------------------
  return {
    // State
    currentBSYear,
    currentBSMonth,
    calendarData,
    isLoading,
    isYearLoading,
    isMonthLoading,
    isInputLoading,
    calendarMode,
    isIndexedDBInitialized,
    showMonthDropdown,
    showYearDropdown,
    selectedDate,
    selectedBSKey,
    selectedADKey,
    primaryADMonth,
    primaryADYear,

    // Refs
    titleRef,

    // Setters
    setShowMonthDropdown,
    setShowYearDropdown,
    setSelectedDate,
    setSelectedBSKey,
    setSelectedADKey,

    // Helpers
    getDataForKey,
    getADMonthRange,
    getDisplayMonthYear,
    getDropdownDisplayValues,

    // Handlers
    handleDayClick,
    handlePrevMonth,
    handleNextMonth,
    handleCalendarModeChange,
    handleMonthSelect,
    handleYearSelect,
    handleInputDate,
    fetchMonthData,
    fetchAllBSMonthsForADMonth,
    navigateToValuePropMonth,
  };
};
